import type { ListResult } from "@workspace/base-api";
import { apiUrl, appFetch, ensureOk, fetchJson } from "@workspace/base-api";
import type { OperationItem } from "@workspace/operations";
import type { IdAndLabel } from "@workspace/shared";

import type {
	AppInstance,
	FeatureCatalog,
	PublicFeatureCatalog,
} from "./types";

export const appInstanceApi = {
	async get(tenant: string) {
		const url = new URL(`/app-instance`, apiUrl);

		return await fetchJson<OperationItem<AppInstance>>(url.toString(), {
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getOptionsCurrent: (tenant: string) => {
		return {
			queryKey: [tenant, "appInstance"],
			queryFn: () => appInstanceApi.get(tenant),
		};
	},
	async getFeatureCatalog(tenant: string) {
		const url = new URL(`/app-instance/features/catalog`, apiUrl);

		return await fetchJson<FeatureCatalog>(url.toString(), {
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getFeatureCatalogOptions: (tenant: string) => {
		return {
			queryKey: [tenant, "appInstance", "feature-catalog"],
			queryFn: () => appInstanceApi.getFeatureCatalog(tenant),
		};
	},
	async getPublicFeatureCatalog() {
		const url = new URL(`/public/onboarding/features/catalog`, apiUrl);
		return await fetchJson<PublicFeatureCatalog>(url.toString(), {});
	},
	getPublicFeatureCatalogOptions: () => {
		return {
			queryKey: ["public", "onboarding", "feature-catalog"],
			queryFn: () => appInstanceApi.getPublicFeatureCatalog(),
		};
	},
	async enableFeature(tenant: string, key: string) {
		const url = new URL(
			`/app-instance/features/${encodeURIComponent(key)}/enable`,
			apiUrl,
		);

		return await fetchJson<{ key: string; installed: boolean }>(
			url.toString(),
			{
				method: "POST",
				headers: {
					tenant: tenant || "",
					"Content-Type": "application/json",
				},
				body: "{}",
			},
		);
	},
	async disableFeature(tenant: string, key: string) {
		const url = new URL(
			`/app-instance/features/${encodeURIComponent(key)}/disable`,
			apiUrl,
		);

		return await fetchJson<{ key: string; installed: boolean }>(
			url.toString(),
			{
				method: "POST",
				headers: {
					tenant: tenant || "",
					"Content-Type": "application/json",
				},
				body: "{}",
			},
		);
	},
	async getPublished() {
		const url = new URL(`/api/app-instances`, apiUrl);

		return await fetchJson<
			ListResult<{
				label: string;
				key: string;
				logo: string;
				features: AppInstance["features"];
				status: AppInstance["status"];
				lastModified: string;
				createdAt: string | null;
			}>
		>(url.toString(), {});
	},
	getPublishedOptions: () => {
		return {
			queryKey: ["appInstances", "published"],
			queryFn: () => appInstanceApi.getPublished(),
		};
	},
	async getForCurrentUser() {
		const url = new URL(`/app-instances/current-user`, apiUrl);

		return await fetchJson<
			ListResult<{
				label: string;
				key: string;
				logo: string;
				features: AppInstance["features"];
				status: AppInstance["status"];
				lastModified: string;
				createdAt: string | null;
			}>
		>(url.toString(), {});
	},
	getForCurrentUserOptions: () => {
		return {
			queryKey: ["appInstances"],
			queryFn: () => appInstanceApi.getForCurrentUser(),
		};
	},
	getAvailablePermission(
		tenant: string,
		options?: { filterByUserAccess?: boolean; venueId?: string },
	) {
		const url = new URL(`/app-instances/permissions`, apiUrl);

		// Default to filtering by user access (prevents privilege escalation)
		const filterByUserAccess = options?.filterByUserAccess ?? true;
		if (!filterByUserAccess) {
			url.searchParams.set("filterByUserAccess", "false");
		}
		if (options?.venueId) {
			url.searchParams.set("venueId", options.venueId);
		}

		return fetchJson<{ label: string; permissions: IdAndLabel[] }[]>(
			url.toString(),
			{
				headers: {
					tenant: tenant || "",
				},
			},
		);
	},
	getAvailablePermissionOptions: (
		tenant: string,
		options?: { filterByUserAccess?: boolean; venueId?: string },
	) => {
		return {
			queryKey: [tenant, "app-instances", "permissions", options],
			queryFn: () => appInstanceApi.getAvailablePermission(tenant, options),
		};
	},

	// System management endpoints
	async getAll() {
		const url = new URL(`/system/app-instances`, apiUrl);

		return await fetchJson<AppInstance[]>(url.toString(), {
			method: "GET",
		});
	},
	getAllOptions: () => {
		return {
			queryKey: ["system", "app-instances"],
			queryFn: () => appInstanceApi.getAll(),
		};
	},

	async deleteTenant(tenant: string) {
		const url = new URL(`/system/app-instances/${tenant}`, apiUrl);
		console.log("Deleting tenant at URL:", url.toString());
		return await ensureOk(
			await appFetch(url.toString(), {
				method: "DELETE",
			}),
		);
	},
	deleteTenantOptions: (tenant: string) => {
		return {
			mutationKey: ["system", "app-instances", "delete", tenant],
			mutationFn: () => appInstanceApi.deleteTenant(tenant),
		};
	},

	async getById(id: string) {
		const url = new URL(`/system/app-instances/${id}`, apiUrl);

		return await fetchJson<{
			appInstance: AppInstance;
			events: {
				id: string;
				type: string;
				timestamp: string;
				data?: Record<string, unknown>;
			}[];
		}>(url.toString(), {
			method: "GET",
		});
	},
	getByIdOptions: (id: string) => {
		return {
			queryKey: ["system", "app-instances", id],
			queryFn: () => appInstanceApi.getById(id),
		};
	},
};
